import React from 'react';
import NumberFlowReact, { useCanAnimate } from '@number-flow/react';

import { Skeleton } from '../../../../components';
import { Root, Affix } from './LiveNumber.styles';

export type LiveNumberProps = {
  value: number;
  /** Currency/marker prefix ("$"). Rendered as a static adjacent node. */
  prefix?: string;
  /** Unit suffix ("%"). Rendered as a static adjacent node. */
  suffix?: string;
  /** Fixed fraction digits (0 for counts/currency, 2 for RTP / avg bet). */
  decimals?: number;
  /** While true, show a sheen placeholder sized to the figure (the endpoint is loading). */
  loading?: boolean;
  /** Not wired to an API field yet. Render a `?` (e.g. Player Balance). */
  missing?: boolean;
  /** The endpoint is failing with nothing cached. Render `N/A` instead of a fake `0`. */
  error?: boolean;
  /** Real data arrived but the value is undefined (a ratio dividing by zero). Render a `–`. */
  indeterminate?: boolean;
  className?: string;
};

/**
 * The figure itself — odometer-rolls from 0 to `value` on reveal, then tweens
 * between values on every later update via NumberFlow. Kept private so the roll-up
 * starts when the figure is revealed (not while it sits under a skeleton).
 *
 * NumberFlow only animates on a value *change*, so the entrance is driven by
 * starting the displayed value at 0 and bumping it to the target after mount.
 * Under reduced motion the figure starts at the target (no roll, no flash of 0).
 */
const Figure = ({
  value,
  prefix,
  suffix,
  decimals,
  className,
}: {
  value: number;
  prefix: string;
  suffix: string;
  decimals: number;
  className?: string;
}): React.ReactElement => {
  const canAnimate = useCanAnimate({ respectMotionPreference: true });
  const [display, setDisplay] = React.useState(() => (canAnimate ? 0 : value));
  React.useEffect(() => {
    setDisplay(value);
  }, [value]);

  return (
    <Root className={className}>
      {prefix ? <Affix>{prefix}</Affix> : null}
      <NumberFlowReact
        value={display}
        format={{ minimumFractionDigits: decimals, maximumFractionDigits: decimals }}
        locales="en-US"
        animated={canAnimate}
      />
      {suffix ? <Affix>{suffix}</Affix> : null}
    </Root>
  );
};

/**
 * A live figure. The currency / percent affixes are static adjacent nodes; the
 * number rolls up from 0 on reveal and tweens between values on later updates
 * (mirrors the area's `StatValue`). A pure numeric value — not translatable copy —
 * so it renders as plain `en-US` nodes. While `loading`, a placeholder sized to the
 * figure stands in (inside `Root`, so it inherits the surrounding font).
 */
export const LiveNumber = ({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  loading = false,
  missing = false,
  error = false,
  indeterminate = false,
  className,
}: LiveNumberProps): React.ReactElement => {
  // A placeholder marker takes precedence over `loading`: the figure has no value
  // coming, so show the marker immediately rather than a skeleton that resolves to
  // nothing. Three markers, by cause: `?` not wired to an API yet, `N/A` the API is
  // failing, `–` the value is undefined this period (a ratio dividing by zero) despite
  // real data. Affixes are dropped (a bare `?` reads cleaner than `$?`).
  if (missing || error || indeterminate) {
    const marker = missing ? '?' : error ? 'N/A' : '–';
    return (
      <Root className={className}>
        <Affix>{marker}</Affix>
      </Root>
    );
  }

  if (loading) {
    const approx = `${prefix}${Math.round(value).toLocaleString('en-US')}${suffix}`;
    return (
      <Root className={className}>
        <Skeleton width={`${Math.max(2, approx.length)}ch`} height="0.8em" radius={5} />
      </Root>
    );
  }

  return <Figure value={value} prefix={prefix} suffix={suffix} decimals={decimals} className={className} />;
};

export default LiveNumber;
